import React, { useState } from "react"

import styled from "styled-components/native"

import { MediaImageFile, MediaUrl } from "@treefort/api-spec"
import { getOptimizedImageSource } from "@treefort/lib/get-optimized-image-source"

import { ImageWithFade } from "./image-with-fade"
import LockedContentIndicator from "./locked-content-indicator"
import { useTokens } from "./tokens-provider"

const BLURHASH_RESOLUTION = 2

const Container = styled.View<{
  $sourceLength: number
  $size?: number
  $backgroundColor: string
}>`
  position: relative;
  border-radius: ${(props) => props.theme.thumbnailArtwork.borderRadius}px;
  overflow: hidden;
  background-color: ${(props) => props.$backgroundColor};
  width: ${(props) => (props.$size ? `${props.$size}px` : "100%")};
  aspect-ratio: 1;
  display: flex;
  ${(props) =>
    props.$sourceLength === 1
      ? `align-items: center; justify-content: center;`
      : `flex-direction: row; flex-wrap: wrap;`}
`

const GridImage = styled(ImageWithFade)`
  width: 50%;
  height: 50%;
`

const SingleImage = styled(ImageWithFade)<{ $containAspectRatio?: number }>`
  ${(props) => {
    if (props.$containAspectRatio) {
      return `
        width: ${props.$containAspectRatio >= 1 ? "90%" : undefined};
        height: ${props.$containAspectRatio < 1 ? "90%" : undefined};
        aspect-ratio: ${props.$containAspectRatio};
        border-radius: ${props.theme.thumbnailArtwork.borderRadius}px;
      `
    } else {
      return `
        width: 100%;
        height: 100%;
      `
    }
  }}
`

/**
 * Renders a thumbnail. Can take a single piece of artwork to fill the thumbnail
 * or multiple pieces to render in a 4x4 grid.
 */
export function SquareThumbnail({
  source,
  size,
  showLockIcon,
  sourceAspectRatio,
  blurhash,
  alpha,
}: {
  /**
   * If an array of images are provided then a grid of repeating images will be
   * rendered for the thumbnail.
   */
  source?: string | Array<string | undefined>
  /**
   * The width/height of the thumbnail. If omitted the thumbnail will fill 100%
   * of its parent's width.
   */
  size?: number
  /**
   * Show a locked content indicator over the thumbnail
   */
  showLockIcon?: boolean
  /**
   * Provide this along with a blurhash to allow the thumbnail to be shown in a
   * contained format to avoid excessive cropping.
   */
  sourceAspectRatio?: number
  /**
   * If the thumbnail has a blurhash then provide that here. We'll render the
   * blurhash in the background and contain the actual thumbnail to avoid
   * cropping.
   */
  blurhash?: string
  /**
   * If the thumbnail has transparency then set this to true. We'll render a
   * transparent background once the image has loaded so the transparency works
   * as expected.
   */
  alpha?: boolean
}): React.JSX.Element {
  const { tokens } = useTokens()
  const [loaded, setLoaded] = useState(false)
  const sourceArray = (Array.isArray(source) ? source : [source]).filter(
    (source): source is string => typeof source === "string",
  )
  const sourceLength = sourceArray.length
  const optimizedImageSize = size ?? tokens.thumbnailArtwork.maxSourceSize
  // If the image doesn't have transparency, it does have a blurhash, and it
  // would be cropped by more than 20% if presented in "cover" mode, then
  // display it in "contain" mode instead with the blurhash behind it
  const containSingleImageWithBlurhash =
    !alpha &&
    blurhash &&
    sourceAspectRatio &&
    Math.abs(1 - sourceAspectRatio) > 0.2
  return (
    <Container
      $sourceLength={sourceLength}
      $size={size}
      $backgroundColor={
        alpha && loaded ? "transparent" : tokens.colors.loading.image
      }
    >
      {sourceLength > 1 ? (
        // Expand the array to 4 items and ensure that the same item is never
        // shown next to itself.
        [
          sourceArray[0],
          sourceArray[1 % sourceLength],
          sourceArray[(sourceLength === 2 ? 3 : 2) % sourceLength],
          sourceArray[(sourceLength === 2 ? 2 : 3) % sourceLength],
        ].map((source, i) => (
          <GridImage
            key={`${source}-${i}`}
            source={{
              uri: getOptimizedImageSource(source, optimizedImageSize),
            }}
            resizeMode="cover"
          />
        ))
      ) : sourceLength === 1 ? (
        <SingleImage
          $containAspectRatio={
            containSingleImageWithBlurhash ? sourceAspectRatio : undefined
          }
          blurhash={
            containSingleImageWithBlurhash
              ? {
                  hash: blurhash,
                  resolutionX: BLURHASH_RESOLUTION,
                  resolutionY: BLURHASH_RESOLUTION,
                  width: "100%",
                  height: "100%",
                }
              : undefined
          }
          source={{
            uri: getOptimizedImageSource(sourceArray[0], optimizedImageSize),
          }}
          resizeMode={
            alpha || containSingleImageWithBlurhash ? "contain" : "cover"
          }
          onLoad={() => setLoaded(true)}
        />
      ) : null}
      {showLockIcon ? <LockedContentIndicator /> : null}
    </Container>
  )
}

/**
 * A square thumbnail that accepts media as an input.
 */
export function SquareMediaThumbnail({
  media,
  size,
  showLockIcon,
}: {
  /**
   * If an array of images are provided then a grid of repeating images will be
   * rendered for the thumbnail.
   */
  media?:
    | MediaImageFile
    | MediaUrl
    | Array<MediaImageFile | MediaUrl | undefined>
  /**
   * The width/height of the thumbnail. If omitted the thumbnail will fill 100%
   * of its parent's width.
   */
  size?: number
  /**
   * Show a locked content indicator over the thumbnail
   */
  showLockIcon?: boolean
}) {
  const mediaArray = Array.isArray(media) ? media : [media]

  const urls = mediaArray.map((media) => {
    switch (media?.type) {
      case "imageFile":
        return media.original.url
      case "url":
        return media.url
    }
  })

  // If we're dealing with a single image file then we will do some fun tricks
  // with transparency and blurhash
  const onlyImageFile =
    mediaArray.length === 1
      ? mediaArray.find(
          (media): media is MediaImageFile => media?.type === "imageFile",
        )?.original
      : undefined
  const { alpha, blurhash, width, height } = onlyImageFile || {}

  return (
    <SquareThumbnail
      source={urls}
      sourceAspectRatio={width && height ? width / height : undefined}
      size={size}
      showLockIcon={showLockIcon}
      alpha={alpha}
      blurhash={blurhash}
    />
  )
}
